import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/authContext';
import { useSite } from '../../context/siteContext';
import { createCase } from '../../services/createCaseFormService';
import { useOptions } from '../../context/optionContext';
import {
	parseEmergencyPhones,
	resolveCaseSafetyConfig,
	resolveCaseTypeConfig,
	resolveEmergencyNumberForRegion,
	resolveSupportSettings,
	SUPPORT_DEFAULT_REGION_PHONES,
} from '../../utilities/supportSettings';
import PhoneInput from "../PhoneInput/PhoneInput";
import { resolvePhoneState } from "../../utilities/phoneUtils";

const EMPTY_FORM = {
	case_title: '',
	case_description: '',
	case_type: '',
	case_type_label: '',
	case_safety: '',
	requester_name: '',
	requester_email: '',
	requester_phone: '',
	machine_number: '',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreateCaseForm = ({ open, setOpen, machine }) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const { selectedSite, selectedSiteData } = useSite();
	const { optionKeys } = useOptions();
	const [errors, setErrors] = useState({});
	const [form, setForm] = useState(EMPTY_FORM);
	const [submitting, setSubmitting] = useState(false);
	const supportSettings = useMemo(() => resolveSupportSettings(optionKeys || {}), [optionKeys]);
	const fieldVisibility = supportSettings.createCaseFieldVisibility || {};
	const caseTypeConfig = useMemo(() => resolveCaseTypeConfig(optionKeys || {}), [optionKeys]);
	const caseSafetyConfig = useMemo(() => resolveCaseSafetyConfig(optionKeys || {}), [optionKeys]);
	const emergencyPhone = useMemo(() => {
		if (!supportSettings.createCaseShowEmergencyMessage) return null;
		const configured = parseEmergencyPhones(optionKeys?.customerLocationEmergencyPhones, SUPPORT_DEFAULT_REGION_PHONES);
		return resolveEmergencyNumberForRegion({
			source: supportSettings.createCaseEmergencyNumberSource,
			manualNumber: supportSettings.createCaseEmergencyNumberManual,
			userRegion: user?.region,
			phones: configured,
		});
	}, [supportSettings, user?.region, optionKeys]);

	const buildInitialForm = useCallback(() => {
		const phoneState = resolvePhoneState({
			phoneValue: user?.user_phone || "",
			countryLabel: user?.country || user?.user_meta?.country || "",
			phoneCountryIso2: user?.phone_country_iso2 || user?.user_meta?.phone_country_iso2 || "",
			phoneCountryCode: user?.phone_country_code || user?.user_meta?.phone_country_code || "",
		});
		const selectedCaseType = (caseTypeConfig.options || []).find((option) => option?.selected && option?.value) || null;
		const selectedCaseSafety = (caseSafetyConfig.options || []).find((option) => option?.selected && option?.value) || null;
		return {
			...EMPTY_FORM,
			requester_name: user?.user_display_name || '',
			requester_email: user?.user_email || '',
			requester_phone: phoneState.phoneValue,
			phone_country_iso2: phoneState.meta.phone_country_iso2,
			phone_country_code: phoneState.meta.phone_country_code,
			machine_number: machine?.machine_number || '',
			site_number: selectedSite,
			site_id: selectedSiteData?.site_id || null,
			case_type: selectedCaseType?.value || '',
			case_type_label: selectedCaseType?.label || '',
			case_safety: selectedCaseSafety?.value || '',
		};
	}, [user, machine, selectedSite, selectedSiteData?.site_id, caseTypeConfig, caseSafetyConfig]);

	//   Reset form + errors on open/close so old data/validations don't persist
	useEffect(() => {
		if (open) {
			setErrors({});
			setForm(buildInitialForm());
		} else {
			setErrors({});
			setForm(EMPTY_FORM);
		}
	}, [open, buildInitialForm]);

	const clearFieldError = (field) => {
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const validate = () => {
		const e = {};

		if (!form.case_title?.trim()) e.case_title = t('UI_TEXT.CASE_TITLE_IS_REQUIRED');
		if (!form.case_description?.trim()) e.case_description = t('UI_TEXT.DESCRIPTION_IS_REQUIRED');
		if (!form.case_type?.trim()) e.case_type = t('UI_TEXT.CASE_TYPE_IS_REQUIRED');
		if (caseSafetyConfig.visible !== false && !form.case_safety?.trim()) e.case_safety = 'Safety selection is required';

		// ✅ Required like Contact CCR
		if (fieldVisibility.requester_name !== false && !form.requester_name?.trim()) e.requester_name = t('UI_TEXT.REQUESTER_NAME_IS_REQUIRED');

		if (fieldVisibility.requester_email !== false && !form.requester_email?.trim()) {
			e.requester_email = t('UI_TEXT.REQUESTER_EMAIL_IS_REQUIRED');
		} else if (fieldVisibility.requester_email !== false && !emailRegex.test(form.requester_email)) {
			e.requester_email = 'Invalid email';
		}

		if (fieldVisibility.machine_number !== false && !form.machine_number?.trim()) e.machine_number = t('UI_TEXT.MACHINE_NUMBER_IS_REQUIRED');

		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const onSubmit = async () => {
		if (!validate()) return;

		setSubmitting(true);
		try {
			await createCase(form);

			// close modal first
			setOpen(false);

			//   Success message
			await Swal.fire({
				icon: 'success',
				title: t('UI_TEXT.CASE_CREATED'),
				text: t('UI_TEXT.YOUR_CASE_HAS_BEEN_SUBMITTED_SUCCESSFULLY_OUR_TEAM_WILL_REVIEW_IT_SHORTLY'),
				confirmButtonText: t('GENERAL.OK'),
				allowOutsideClick: false,
			});
		} catch (err) {
			console.error(err);

			await Swal.fire({
				icon: 'error',
				title: t('UI_TEXT.SUBMISSION_FAILED'),
				text: t('UI_TEXT.SOMETHING_WENT_WRONG_WHILE_CREATING_THE_CASE_PLEASE_TRY_AGAIN'),
				confirmButtonText: t('GENERAL.OK'),
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<ModalWrapper
			open={open}
			setOpen={setOpen}
			title={t('LABELS.NEW_CASE', { defaultValue: "New Case" })}
			subtitle={t('UI_TEXT.ENTER_CASE_DETAILS_TO_NOTIFY_ADMINS', { defaultValue: "Enter case details to notify admins." })}
			content={
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<TextField
						label={t('UI_TEXT.CASE_TITLE_1')}
						fullWidth
						size="small"
						margin="normal"
						required
						value={form.case_title}
						onChange={(e) => {
							clearFieldError('case_title');
							setForm((p) => ({ ...p, case_title: e.target.value }));
						}}
						error={!!errors.case_title}
						helperText={errors.case_title || ''}
					/>

					<TextField
						label={t('UI_TEXT.CASE_DESCRIPTION')}
						fullWidth
						multiline
						rows={4}
						size="small"
						margin="normal"
						required
						value={form.case_description}
						onChange={(e) => {
							clearFieldError('case_description');
							setForm((p) => ({ ...p, case_description: e.target.value }));
						}}
						error={!!errors.case_description}
						helperText={errors.case_description || ''}
					/>

					<TextField
						label={t('UI_TEXT.CASE_TYPE_1')}
						select
						fullWidth
						size="small"
						margin="normal"
						required
						SelectProps={{
							MenuProps: {
								slotProps: {
									root: { className: "modal-menu-popover" },
								},
							},
						}}
						value={form.case_type}
						onChange={(e) => {
							clearFieldError('case_type');
							const selectedValue = e.target.value;
							const selectedOption = (caseTypeConfig.options || []).find((option) => option.value === selectedValue);
							setForm((p) => ({
								...p,
								case_type: selectedValue,
								case_type_label: selectedOption?.label || selectedValue,
							}));
						}}
						error={!!errors.case_type}
						helperText={errors.case_type || ''}
					>
						{caseTypeConfig.default?.label && (
							<MenuItem
								value={caseTypeConfig.default?.value || ''}
								disabled={caseTypeConfig.default?.disabled !== false}
							>
								{caseTypeConfig.default?.label}
							</MenuItem>
						)}
						{(caseTypeConfig.options || []).map((option) => (
							<MenuItem key={`${option.value}-${option.label}`} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
					{caseSafetyConfig.visible !== false && (
						<TextField
							label={caseSafetyConfig.default?.label || 'Safety Issue/ Shutdown?'}
							select
							fullWidth
							size="small"
							margin="normal"
							required
							SelectProps={{
								MenuProps: {
									slotProps: {
										root: { className: "modal-menu-popover" },
									},
								},
							}}
							value={form.case_safety}
							onChange={(e) => {
								clearFieldError('case_safety');
								setForm((p) => ({ ...p, case_safety: e.target.value }));
							}}
							error={!!errors.case_safety}
							helperText={errors.case_safety || ''}
						>
							{(caseSafetyConfig.options || []).map((option) => (
								<MenuItem key={`${option.value}-${option.label}`} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					)}

					{fieldVisibility.requester_name !== false && (
					<TextField
						label={t('UI_TEXT.REQUESTER_NAME', { defaultValue: "Requester Name" })}
						fullWidth
						size="small"
						margin="normal"
						required
						value={form.requester_name}
						InputProps={{ readOnly: true }}
						error={!!errors.requester_name}
						helperText={errors.requester_name || ''}
					/>)}

					{fieldVisibility.requester_email !== false && (
					<TextField
						label={t('UI_TEXT.REQUESTER_EMAIL', { defaultValue: "Requester Email" })}
						fullWidth
						size="small"
						margin="normal"
						required
						value={form.requester_email}
						InputProps={{ readOnly: true }}
						error={!!errors.requester_email}
						helperText={errors.requester_email || ''}
					/>)}

					{fieldVisibility.requester_phone !== false && (
					<PhoneInput
						label="Requester Phone"
						size="small"
						value={form.requester_phone}
						onChange={() => {}}
						countryIso2={form.phone_country_iso2}
						countryCode={form.phone_country_code}
						allowCountryChange={false}
						onCountryChange={() => {}}
						disabled
					/>)}

					{fieldVisibility.machine_number !== false && (
					<TextField
						label="Machine Number"
						fullWidth
						size="small"
						margin="normal"
						required
						value={form.machine_number}
						onChange={(e) => {
							clearFieldError('machine_number');
							setForm((p) => ({ ...p, machine_number: e.target.value }));
						}}
						error={!!errors.machine_number}
						helperText={errors.machine_number || ''}
					/>)}
					{emergencyPhone && (
						<Box sx={{ mt: 1, color: "error.main", fontSize: "0.75rem" }}>
							{t('UI_TEXT.IF_THIS_IS_AN_EMERGENCY_PLEASE_CALL_THE_EMERGENCY_NUMBER')}{" "}
							<a href={`tel:${emergencyPhone}`} style={{ color: "inherit", textDecoration: "underline", fontWeight: 600 }}>
								{emergencyPhone}
							</a>
						</Box>
					)}
				</Box>
			}
			actions={
				<>
					<Button onClick={() => setOpen(false)} disabled={submitting}>
					{t('BUTTON.CANCEL')}
					</Button>
					<Button variant="contained" onClick={onSubmit} disabled={submitting}>
						{submitting ? 'Submitting…' : t('BUTTON.SUBMIT')}
					</Button>
				</>
			}
		/>
	);
};

export default CreateCaseForm;
